import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaStore, FaPhone, FaShareAlt, FaCog, FaCreditCard, FaLock, FaImage, FaTimes } from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import { useSettings } from '../../context/SettingsContext';
import { LoadingState } from '../../components/Loading';

const AdminSettings = () => {
  const toast = useToast();
  const { refreshSettings } = useSettings();
  const [settings, setSettings] = useState({
    siteName: '',
    siteDescription: '',
    contactEmail: '',
    contactPhone: '',
    socialMedia: {
      facebook: '', twitter: '', instagram: '', linkedin: '', youtube: '', pinterest: '', tiktok: '', whatsapp: '',
    },
    currency: 'NGN',
    taxRate: 0,
    shippingFee: 0,
    freeShippingThreshold: 0,
    heroEyebrow: '',
    heroTitle: '',
    heroSubtitle: '',
    heroImage: '',
    paystackEnabled: false,
    paystackPublicKey: '',
    paystackSecretKey: '',
    paystackSecretKeySet: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get('/api/settings');
      setSettings({
        ...settings,
        ...res.data,
        socialMedia: { ...settings.socialMedia, ...(res.data.socialMedia || {}) },
      });
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('social.')) {
      const key = name.split('.')[1];
      setSettings({
        ...settings,
        socialMedia: { ...settings.socialMedia, [key]: value },
      });
    } else {
      setSettings({ ...settings, [name]: type === 'checkbox' ? checked : value });
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingBanner(true);
    try {
      const data = new FormData();
      data.append('image', file);
      const res = await axios.post('/api/settings/upload', data);
      setSettings((prev) => ({ ...prev, heroImage: res.data.url }));
      toast.success('Banner uploaded — remember to save');
    } catch (error) {
      toast.error('Banner upload failed');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put('/api/settings', settings);
      await refreshSettings();
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <div>
      <div className="admin-header">
        <div>
          <h1>Settings</h1>
          <p className="admin-header-subtitle">Configure your store</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 900 }}>
        {/* General */}
        <div className="card">
          <div className="card-header">
            <h2><FaStore style={{ marginRight: 10, color: 'var(--primary-600)' }} /> General</h2>
          </div>
          <div className="form-group">
            <label>Site name</label>
            <input type="text" name="siteName" value={settings.siteName} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Site description</label>
            <textarea name="siteDescription" rows="3" value={settings.siteDescription} onChange={handleChange} />
            <small>Shown in the footer and meta description</small>
          </div>
        </div>

        {/* Homepage Banner */}
        <div className="card">
          <div className="card-header">
            <h2><FaImage style={{ marginRight: 10, color: 'var(--primary-600)' }} /> Homepage banner</h2>
          </div>

          <div className="form-group">
            <label>Banner image</label>
            {settings.heroImage ? (
              <div style={{ position: 'relative', marginBottom: 12, borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--gray-200)' }}>
                <img
                  src={settings.heroImage}
                  alt="Banner preview"
                  style={{ width: '100%', maxHeight: 220, objectFit: 'cover', display: 'block' }}
                />
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, heroImage: '' })}
                  style={{
                    position: 'absolute', top: 10, right: 10, width: 32, height: 32,
                    borderRadius: '50%', background: 'rgba(0,0,0,0.7)', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  title="Remove banner"
                >
                  <FaTimes />
                </button>
              </div>
            ) : (
              <label
                htmlFor="bannerUpload"
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  padding: '36px 20px', border: '2px dashed var(--gray-300)',
                  borderRadius: 'var(--radius)', background: 'var(--gray-50)', cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                <FaImage style={{ fontSize: 28, color: 'var(--primary-600)' }} />
                <strong style={{ fontSize: 14 }}>
                  {uploadingBanner ? 'Uploading...' : 'Click to upload banner'}
                </strong>
                <small style={{ color: 'var(--gray-500)' }}>Recommended 1600×800px · JPG or PNG up to 5MB</small>
              </label>
            )}
            <input
              id="bannerUpload"
              type="file"
              accept="image/*"
              onChange={handleBannerUpload}
              style={{ display: 'none' }}
            />
            {settings.heroImage && (
              <label
                htmlFor="bannerUpload"
                className="btn btn-secondary btn-sm"
                style={{ marginTop: 4, cursor: 'pointer' }}
              >
                {uploadingBanner ? 'Uploading...' : 'Replace image'}
              </label>
            )}
          </div>

          <div className="form-group">
            <label>Eyebrow text</label>
            <input
              type="text" name="heroEyebrow"
              value={settings.heroEyebrow} onChange={handleChange}
              placeholder="Centre Techniques · Est. 1990"
            />
            <small>The small label above the headline</small>
          </div>

          <div className="form-group">
            <label>Headline</label>
            <input
              type="text" name="heroTitle"
              value={settings.heroTitle} onChange={handleChange}
              placeholder="Where music comes to life"
            />
          </div>

          <div className="form-group">
            <label>Subtitle</label>
            <textarea
              name="heroSubtitle" rows="3"
              value={settings.heroSubtitle} onChange={handleChange}
              placeholder="Short paragraph describing your store..."
            />
          </div>
        </div>

        {/* Contact */}
        <div className="card">
          <div className="card-header">
            <h2><FaPhone style={{ marginRight: 10, color: 'var(--primary-600)' }} /> Contact</h2>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="contactEmail" value={settings.contactEmail} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input type="tel" name="contactPhone" value={settings.contactPhone} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Social */}
        <div className="card">
          <div className="card-header">
            <h2><FaShareAlt style={{ marginRight: 10, color: 'var(--primary-600)' }} /> Social media</h2>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Facebook</label>
              <input type="url" name="social.facebook" value={settings.socialMedia.facebook} onChange={handleChange} placeholder="https://facebook.com/yourpage" />
            </div>
            <div className="form-group">
              <label>Twitter / X</label>
              <input type="url" name="social.twitter" value={settings.socialMedia.twitter} onChange={handleChange} placeholder="https://twitter.com/yourhandle" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Instagram</label>
              <input type="url" name="social.instagram" value={settings.socialMedia.instagram} onChange={handleChange} placeholder="https://instagram.com/yourhandle" />
            </div>
            <div className="form-group">
              <label>LinkedIn</label>
              <input type="url" name="social.linkedin" value={settings.socialMedia.linkedin} onChange={handleChange} placeholder="https://linkedin.com/company/..." />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>YouTube</label>
              <input type="url" name="social.youtube" value={settings.socialMedia.youtube} onChange={handleChange} placeholder="https://youtube.com/..." />
            </div>
            <div className="form-group">
              <label>Pinterest</label>
              <input type="url" name="social.pinterest" value={settings.socialMedia.pinterest} onChange={handleChange} placeholder="https://pinterest.com/..." />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>TikTok</label>
              <input type="url" name="social.tiktok" value={settings.socialMedia.tiktok} onChange={handleChange} placeholder="https://tiktok.com/@yourhandle" />
            </div>
            <div className="form-group">
              <label>WhatsApp</label>
              <input type="text" name="social.whatsapp" value={settings.socialMedia.whatsapp} onChange={handleChange} placeholder="https://wa.me/2348001234567" />
              <small>WhatsApp link or "wa.me" URL</small>
            </div>
          </div>
        </div>

        {/* Store */}
        <div className="card">
          <div className="card-header">
            <h2><FaCog style={{ marginRight: 10, color: 'var(--primary-600)' }} /> Store configuration</h2>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Currency</label>
              <select name="currency" value={settings.currency} onChange={handleChange}>
                <option value="NGN">NGN — Nigerian Naira (₦)</option>
                <option value="USD">USD — US Dollar ($)</option>
                <option value="EUR">EUR — Euro (€)</option>
                <option value="GBP">GBP — British Pound (£)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Tax rate (%)</label>
              <input type="number" name="taxRate" value={settings.taxRate} onChange={handleChange} step="0.01" min="0" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Shipping fee</label>
              <input type="number" name="shippingFee" value={settings.shippingFee} onChange={handleChange} step="0.01" min="0" />
            </div>
            <div className="form-group">
              <label>Free shipping threshold</label>
              <input type="number" name="freeShippingThreshold" value={settings.freeShippingThreshold} onChange={handleChange} step="0.01" min="0" />
              <small>Orders above this get free shipping (0 to disable)</small>
            </div>
          </div>
        </div>

        {/* Paystack */}
        <div className="card">
          <div className="card-header">
            <h2><FaCreditCard style={{ marginRight: 10, color: 'var(--primary-600)' }} /> Paystack payments</h2>
          </div>

          <label className="checkbox-row">
            <input
              type="checkbox"
              name="paystackEnabled"
              checked={settings.paystackEnabled}
              onChange={handleChange}
            />
            <div>
              <strong>Enable Paystack</strong>
              <small>Accept card, bank transfer, and USSD payments at checkout</small>
            </div>
          </label>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label>Public key</label>
            <input
              type="text"
              name="paystackPublicKey"
              value={settings.paystackPublicKey}
              onChange={handleChange}
              placeholder="pk_test_xxxxxxxxxxxxxxxxxxxx"
            />
            <small>Found in your Paystack Dashboard → Settings → API Keys &amp; Webhooks</small>
          </div>

          <div className="form-group">
            <label>
              Secret key
              {settings.paystackSecretKeySet && (
                <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--success-600)', fontWeight: 600 }}>
                  <FaLock style={{ fontSize: 10, marginRight: 3 }} /> Configured
                </span>
              )}
            </label>
            <input
              type="password"
              name="paystackSecretKey"
              value={settings.paystackSecretKey}
              onChange={handleChange}
              placeholder={settings.paystackSecretKeySet ? '•••••••••• (leave blank to keep current)' : 'sk_test_xxxxxxxxxxxxxxxxxxxx'}
              autoComplete="off"
            />
            <small>Stored securely and never exposed to customers. Leave blank to keep the existing key.</small>
          </div>

          <div className="alert alert-info" style={{ marginTop: 8 }}>
            Set your webhook URL in Paystack to:
            <code style={{ display: 'block', marginTop: 6, fontSize: 13 }}>
              https://yourdomain.com/api/payments/paystack/webhook
            </code>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
